import {useEffect} from 'react';
import {
    useAssistantContext,
    type ContextLayer,
    type ContextLayerValue
} from './components/AssistantContext';
import {formatWeekContextLabel} from '../lib/week-calculator';
import type {ContextEntity, ContextLens, ContextPeriod} from './api/types';

/**
 * What a page publishes for the AI assistant. It is merged with the route (the
 * provider adds the route pattern and URL) and anything selected on the page,
 * sent with every message, stored on the run, and injected into the assistant's
 * instructions server-side. Give records their ids: the assistant passes them
 * straight to its tools instead of looking them up by name.
 */
export interface PageContext {
    /** Breadcrumb of where the user is, e.g. "Insights > Explore > Utilisation". */
    title: string;
    /** The records the page is about, the primary one first (project, person, team, role, document...). */
    entities?: ContextEntity[];
    /** The week window in view. */
    period?: ContextPeriod;
    /** The what-if lens in effect; omit for the published view. */
    lens?: ContextLens;
    /** Active filters as label to value, e.g. { Scope: "All organisation" }. */
    filters?: Record<string, string>;
}

/** Joins breadcrumb parts with " > ", dropping any falsy parts. */
export function buildPath(
    ...parts: Array<string | false | null | undefined>
): string {
    return parts.filter((p): p is string => Boolean(p)).join(' > ');
}

/** A record on the page, for `entities` and `useAssistantSelection`. */
export function contextEntity(
    kind: string,
    id: string,
    name?: string | null,
    detail?: string | null
): ContextEntity {
    return {kind, id, name: name ?? undefined, detail: detail ?? undefined};
}

/**
 * Describes a week window for the assistant: the global week numbers plus the
 * human form "{n} weeks (W26 Jun 22 2026 – W29 Jul 13 2026)" (or "1 week (…)").
 */
export function describeWeekRange(
    startWeek: number,
    numberOfWeeks: number
): ContextPeriod {
    const count = Math.max(1, numberOfWeeks);
    const duration = `${count} week${count === 1 ? '' : 's'}`;
    const from = formatWeekContextLabel(startWeek);
    const toWeek = startWeek + count - 1;
    const label =
        count === 1
            ? `${duration} (${from})`
            : `${duration} (${from} – ${formatWeekContextLabel(toWeek)})`;
    return {fromWeek: startWeek, toWeek, label};
}

function useAssistantLayer(
    layer: ContextLayer,
    value: ContextLayerValue | null
): void {
    const {publish} = useAssistantContext();
    // Re-publish only when the content actually changes (avoids object-identity loops).
    const serialised = JSON.stringify(value ?? null);

    useEffect(() => {
        publish(layer, value === null ? null : {...value});
        return () => publish(layer, null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layer, serialised]);
}

/**
 * Publishes the page's context to the AI assistant while this component is
 * mounted, and clears it on unmount. Each leaf page owns its context: set it
 * once with everything relevant (title, records, period, lens, filters). Pass
 * `null` to publish nothing.
 */
export function useAssistantPageContext(context: PageContext | null): void {
    useAssistantLayer('page', context);
}

/**
 * For a view inside a page that refines what the page published: its period
 * and lens replace the page's, its filters are added, its records appended.
 */
export function useAssistantViewContext(
    context: Partial<PageContext> | null
): void {
    useAssistantLayer('view', context);
}

/**
 * Publishes what the user has selected within the page (a cell, a row, a role),
 * cleared on unmount. The assistant reads "this" and "it" as the selection when
 * there is one.
 */
export function useAssistantSelection(
    selection: ContextEntity[] | ContextEntity | null
): void {
    const list =
        selection === null
            ? null
            : {selection: Array.isArray(selection) ? selection : [selection]};
    useAssistantLayer('selection', list);
}

/**
 * The route layer: the host's layout publishes the route pattern (with
 * `:param` tokens), the real URL and the page's title, so every page reports
 * where the user is even before it publishes anything of its own.
 */
export function useAssistantRouteContext(
    path: string,
    url: string,
    title?: string
): void {
    useAssistantLayer('route', {path, url, title});
}
